import React from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { formatCompactNumber, formatAmount } from '../../Shared/Formatters';
import { useThemePalette } from '../../hooks/useThemePalette';

/**
 * Line chart widget — Trading Density, Predictive Growth, generic time series.
 */
export default function LineChartWidget({ data }) {
    const { t } = useTranslation();
    // Premium skins recolour the line live; null in classic → original blue/cyan.
    const palette = useThemePalette();
    const colors = palette ? [palette[0], palette[1]] : ['#3B82F6', '#06B6D4'];

    if (!data?.data) return null;
    const d = data.data;

    // Predictive Growth shape: { ytdActual, projectedTotal, remainingMonths: [{year, month, projected}] }
    if (d.projectedTotal !== undefined && Array.isArray(d.remainingMonths)) {
        const months = d.remainingMonths;
        const actual = { name: t('Actual'), data: [{ x: t('YTD'), y: d.ytdActual }] };
        const forecast = { name: t('Projected'), data: months.map(m => ({
            x: `${m.year}-${String(m.month).padStart(2, '0')}`,
            y: m.projected,
        })) };
        return renderChart([actual, forecast], colors);
    }

    // Generic series: Array of {label, value} or {date, value}
    if (Array.isArray(d)) {
        const series = [{ name: t('Value'), data: d.map(r => ({ x: r.label || r.date || r._id, y: r.value || r.total || r.totalSales || 0 })) }];
        return renderChart(series, colors);
    }

    return <div style={{ padding: 20 }} className="v3-body">{t('Chart data unavailable.')}</div>;
}

function renderChart(series, colors = ['#3B82F6', '#06B6D4']) {
    const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
    const options = {
        chart: { type: 'line', toolbar: { show: false }, fontFamily: 'Inter, system-ui', background: 'transparent' },
        colors,
        stroke: { width: 2, curve: 'smooth' },
        fill: { type: 'gradient', gradient: { opacityFrom: 0.25, opacityTo: 0.02 } },
        grid: { borderColor: 'var(--v3-chart-grid)', strokeDashArray: 4 },
        xaxis: { labels: { style: { colors: 'var(--v3-text-muted)', fontSize: '10px' } } },
        yaxis: { labels: {
            style: { colors: 'var(--v3-text-muted)', fontSize: '10px', fontFamily: 'JetBrains Mono, monospace' },
            formatter: formatCompactNumber,
        } },
        tooltip: {
            theme: isDark ? 'dark' : 'light',
            y: { formatter: (val) => formatAmount(val) },
        },
        dataLabels: { enabled: false },
        legend: { show: series.length > 1, fontSize: '10px', labels: { colors: 'var(--v3-text-muted)' } },
    };

    return (
        <div style={{ padding: '4px 10px 8px', height: '100%' }}>
            <Chart type="area" options={options} series={series} height="100%" />
        </div>
    );
}
